import React, { useState } from 'react';
import {
  Stack,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { IconLibrary } from '@product-framework/index';
import ModalViewer from '@product-framework/components/Viewers/ModalViewer';
import { isSupportedByModalViewer } from '@product-framework/components/Viewers';
import formatTimeZoneDateTime from '@product-framework/utils/formatTimeZoneDateTime';
import {
  useLocalization,
  SnackbarApiErrorMessage,
  useMasterData,
  getFormattedFileSize,
  getFileExtension,
} from '@ui-framework/index';

function fileName(original) {
  return original?.fileName || '';
}

function searchCriteria(original) {
  const criteria = original?.searchCriteria ?? {};
  const entries = Object.entries(criteria);

  if (!entries.length) {
    return '';
  }

  return (
    <Stack component="span" spacing={0.25} sx={{ py: 0.25 }}>
      {entries.map(([key, value]) => (
        <Typography
          key={key}
          component="span"
          variant="body2"
          sx={{ display: 'block' }}>
          {`${key
            .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
            .replace(/^./, character => character.toUpperCase())}: ${
            typeof value === 'object' ? JSON.stringify(value) : value
          }`}
        </Typography>
      ))}
    </Stack>
  );
}

function dateTimeCell(value) {
  if (!value) return '';

  const { formatMessage } = useLocalization();

  const { localDatetime, systemDatetime } = formatTimeZoneDateTime(value);

  return (
    <Stack component="span" spacing={0.25} sx={{ py: 0.25 }}>
      <Typography component="span" variant="body2" sx={{ display: 'block' }}>
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{ mr: 0.75 }}>
          {formatMessage({
            id: 'DATA_EXPORT_HISTORY_LIST_LOCAL_DATETIME',
            defaultMessage: 'Your Time',
          })}
          :
        </Typography>
        {localDatetime}
      </Typography>
      <Typography component="span" variant="body2" sx={{ display: 'block' }}>
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{ mr: 0.75 }}>
          {formatMessage({
            id: 'DATA_EXPORT_HISTORY_LIST_SYSTEM_DATETIME',
            defaultMessage: 'System Time',
          })}
          :
        </Typography>
        {systemDatetime}
      </Typography>
    </Stack>
  );
}

function createdDateTime(original) {
  return dateTimeCell(original?.createdDateTime);
}

function expirationDateTime(original) {
  return dateTimeCell(original?.expirationDateTime);
}

function status(original) {
  const { dataExportHistoryStatuses } = useMasterData();
  const found = dataExportHistoryStatuses.find(
    statusObj => statusObj.value === original?.status,
  );
  return found?.label || '';
}

function failedReason(original) {
  return original?.failedReason || '';
}

function totalRecords(original) {
  const { formatMessage } = useLocalization();
  const downloaded = original?.totalRecordsDownloaded;
  const requested = original?.totalRecordsRequested;
  if (downloaded == null && requested == null) return '';

  const requestedLabel = formatMessage({
    id: 'DATA_EXPORT_HISTORY_LIST_RECORDS_REQUESTED_LABEL',
    defaultMessage: 'Requested',
  });
  const parts = [`${requestedLabel} : ${requested ?? ''}`];

  if (downloaded != null) {
    const downloadedLabel = formatMessage({
      id: 'DATA_EXPORT_HISTORY_LIST_RECORDS_DOWNLOADED_LABEL',
      defaultMessage: 'Downloaded',
    });
    parts.push(`${downloadedLabel} : ${downloaded}`);
  }

  return parts.join(', ');
}

function exportType(original) {
  return original?.exportType ?? '';
}

function fileSize(original) {
  const size = original?.fileSize;
  if (size == null || size === '') return '';
  return getFormattedFileSize(size);
}

function DataExportHistoryDownloadButton({
  row,
  getApiUrl,
  get,
  formatMessage,
  snackbar,
}) {
  const [loading, setLoading] = useState(false);

  const getPresignedUrl = async () => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/data-export-history/${row?.id}/download`,
    );
    if (response?.ok) {
      return response.data.presignedUrl;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const handleDownload = async () => {
    setLoading(true);
    const presignedUrl = await getPresignedUrl();
    if (presignedUrl) {
      window.open(presignedUrl, '_blank', 'noopener,noreferrer');
    } else {
      snackbar.showMessage({
        type: 'error',
        data: (
          <SnackbarApiErrorMessage
            errors={[
              {
                message: formatMessage({
                  id: 'DATA_EXPORT_HISTORY_DOWNLOAD_FAILED',
                  defaultMessage: 'Failed to download export file',
                }),
              },
            ]}
          />
        ),
      });
    }
    setLoading(false);
  };

  return (
    <Tooltip
      title={formatMessage({
        id: 'DATA_EXPORT_HISTORY_DOWNLOAD_TOOLTIP',
        defaultMessage: 'Download export file',
      })}>
      <IconButton size="small" onClick={handleDownload} disabled={loading}>
        {loading ? (
          <CircularProgress color="inherit" size={18} />
        ) : (
          <IconLibrary.FileDownload />
        )}
      </IconButton>
    </Tooltip>
  );
}

function download(original, _props, accessorFunctionsAdditional) {
  if (original?.status !== 'COMPLETED' || !(Number(original?.fileSize) > 0)) {
    return null;
  }

  const { getApiUrl, get, formatMessage, snackbar } =
    accessorFunctionsAdditional;

  return (
    <DataExportHistoryDownloadButton
      row={original}
      getApiUrl={getApiUrl}
      get={get}
      formatMessage={formatMessage}
      snackbar={snackbar}
    />
  );
}

function DataExportHistoryPreviewButton({
  row,
  getApiUrl,
  get,
  formatMessage,
  snackbar,
}) {
  const [loading, setLoading] = useState(false);
  const [viewer, setViewer] = useState(null);

  const getPresignedUrl = async () => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/data-export-history/${row?.id}/preview`,
    );
    if (response?.ok) {
      return response.data.presignedUrl;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const handlePreview = async () => {
    setLoading(true);
    const presignedUrl = await getPresignedUrl();
    if (presignedUrl) {
      if (isSupportedByModalViewer(row?.fileName)) {
        setViewer({ url: presignedUrl, fileName: row.fileName });
      } else {
        window.open(presignedUrl, '_blank', 'noopener,noreferrer');
      }
    } else {
      snackbar.showMessage({
        type: 'error',
        data: (
          <SnackbarApiErrorMessage
            errors={[
              {
                message: formatMessage({
                  id: 'DATA_EXPORT_HISTORY_PREVIEW_FAILED',
                  defaultMessage: 'Failed to generate preview of export file',
                }),
              },
            ]}
          />
        ),
      });
    }
    setLoading(false);
  };

  return (
    <>
      <Tooltip
        title={formatMessage({
          id: 'DATA_EXPORT_HISTORY_PREVIEW_TOOLTIP',
          defaultMessage: 'Preview file',
        })}>
        <IconButton size="small" onClick={handlePreview} disabled={loading}>
          {loading ? (
            <CircularProgress color="inherit" size={18} />
          ) : (
            <IconLibrary.FilePreview />
          )}
        </IconButton>
      </Tooltip>
      {viewer && (
        <ModalViewer
          open
          onClose={() => setViewer(null)}
          title={viewer.fileName}
          fileType={getFileExtension(viewer.fileName)}
          fileUrl={viewer.url}
        />
      )}
    </>
  );
}

function preview(original, _props, accessorFunctionsAdditional) {
  if (!original?.canPreview || !(Number(original?.fileSize) > 0)) {
    return null;
  }

  const { getApiUrl, get, formatMessage, snackbar } =
    accessorFunctionsAdditional;

  return (
    <DataExportHistoryPreviewButton
      row={original}
      getApiUrl={getApiUrl}
      get={get}
      formatMessage={formatMessage}
      snackbar={snackbar}
    />
  );
}

export {
  fileName,
  searchCriteria,
  createdDateTime,
  expirationDateTime,
  status,
  failedReason,
  totalRecords,
  download,
  preview,
  exportType,
  fileSize,
};
